// Iter161 — Lightweight in-app trial countdown banner.
// Reads day5/day6 events queued by sweep_expired_trials in `trial_events`.
// Independent of Resend — surfaces the countdown to logged-in users
// immediately, with a Subscribe CTA that starts the Stripe checkout flow.
// Dismissable per-event (server-side dismissed_at flag).
import React from "react";
import { AlertTriangle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

export default function TrialCountdownBanner() {
  const [banner, setBanner] = React.useState(null);
  const [demoMode, setDemoMode] = React.useState(false);
  const [checkoutLoading, setCheckoutLoading] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data }, meRes] = await Promise.all([
          api.get("/tier/trial-banner"),
          api.get("/billing/me").catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        if (data?.show) setBanner(data);
        setDemoMode(!!meRes.data?.demo_mode);
      } catch {
        /* silent — banner is non-critical */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismiss = async () => {
    setBanner(null);
    try { await api.post("/tier/trial-banner/dismiss"); } catch { /* silent */ }
  };

  const subscribe = async () => {
    setCheckoutLoading(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        origin: window.location.origin,
        package: "markovlab_monthly",
      });
      if (data?.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      const msg = e.response?.data?.detail;
      if (msg?.code === "not_configured") {
        toast.error("Stripe not yet configured — check back shortly.");
      } else {
        toast.error("Checkout failed to start.");
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!banner) return null;

  const days = banner.days_left;
  const dayLabel =
    days === 0 ? "today" :
    days === 1 ? "in 1 day" :
    `in ${days} days`;

  return (
    <div
      data-testid="trial-countdown-banner"
      className="flex items-center gap-3 px-4 py-2 border-b text-xs"
      style={{
        background: "rgba(245,158,11,0.08)",
        borderColor: "rgba(245,158,11,0.3)",
        color: "var(--hoip-text-primary)",
      }}
    >
      <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-400" />
      <span data-testid="trial-countdown-message" className="flex-1">
        Your trial ends {dayLabel} (day 15). Continuing after that requires a paid plan.
      </span>
      <Button
        size="sm"
        className="h-7 text-xs"
        disabled={checkoutLoading}
        onClick={demoMode
          ? () => { window.location.href = "mailto:info@markovlab.app?subject=MarkovLab%20early%20access"; }
          : subscribe}
        data-testid="trial-countdown-subscribe-btn"
      >
        {demoMode ? "Contact us" : (checkoutLoading ? "Starting…" : "Subscribe")}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        onClick={dismiss}
        data-testid="trial-countdown-dismiss-btn"
        aria-label="Dismiss trial countdown"
      >
        <X className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}
